import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { LdsUserService } from './services/lds-user.service';
import { WsPacketService } from './lds-websocket-module/ws-packet/ws-packet.service';
import { LwProductService } from './lw-product/lw-product.service';
import { DataAnalyticsService } from './data-analytics/data-analytics.service';
import { WsChatService } from './lds-websocket-module/ws-chat/ws-chat.service';
import { StorageMap } from '@ngx-pwa/local-storage';
import { EnergyDataService } from './energy-data/energy-data.service';
import { LdsEnergyFeatureSetsLocalStorage } from './interfaces/data-analytics';
import { LdsEventBusService } from './services/lds-event-bus.service';
import { WebSocketPacket } from './interfaces/all';
import { DemoAccountService } from './demo-account.service';
import { AuthService } from '../auth.service';


@Injectable({
    providedIn: 'root'
})
export class LdsAccessGuard  {
    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        this.eventBus.clearAll();
        const route = this.router.currentNavigation()

        if (route.extras.queryParams && route.extras.queryParams.demo === 'true') {

            this.demo.setDemo(true);
            this.user.initiate();
            this.energyDataService.initiate();
            const res = this.demo.getDemoStructure();

            this.energyDataService.featureSetsWithData = res.featureSetsWithData;
            this.energyDataService.featureSetsWithoutData = res.featureSetsWithoutData;
            const featureSets = res.featureSets;
            this.user.features = res.features;
            this.user._devices = res.devices;
            this.user.rooms = res.rooms;
            for (const key in featureSets) {
                if (featureSets.hasOwnProperty(key)) {
                    this.findEnergyFeature(featureSets[key]);
                }
            }

            // Use parallel loading for demo mode as well
            this.analyticsService.loadEnergySummaryData(this.user.energyFeatureSets);
            this.user.settings = this.demo.getDemoUserSettings();

            return true;
        }

        return new Promise((resolve, reject) => {

            this.user.getUserDetail()
                .then((userDetail) => {
                    this.loadUserFromLocalStorage(userDetail, resolve);
                })
                .catch(() => {
                    this.router.navigate(['/']);
                    resolve(true);
                });
        });
    }

    constructor(private user: LdsUserService, private wsPacketService: WsPacketService, private router: Router, private aRoute: ActivatedRoute,
        protected localStorage: StorageMap, private lwProductService: LwProductService, private analyticsService: DataAnalyticsService,
        private wsChatService: WsChatService, private energyDataService: EnergyDataService, private eventBus: LdsEventBusService, private demo: DemoAccountService, private auth: AuthService) { }

    initiateUser(usrInfo, cb) {
        this.user.initiate();
        this.energyDataService.initiate();
        this.initiateWs(usrInfo);



        this.wsChatService.checkAuth(() => {

            let wsPacket = this.wsPacketService.getPacket('user', 'read', {});
            this.wsChatService._sendMsg(wsPacket)
                .then((userSettings: WebSocketPacket) => {
                    this.user.settings = userSettings;

                });
            this.wsChatService._sendMsg(this.wsPacketService.getPacket('user', 'rootGroups', {}))
                .then((res: WebSocketPacket) => {

                    this.readRootGroups(res, cb);
                });
        })
    }

    initiateWs(usrInfo) {
        if (this.wsChatService.alreadyAuthed()) {
            this.wsChatService.initiateNoAuth(usrInfo.user._id);
        } else {
            this.wsChatService.initiate(usrInfo.user._id);
        }
    }

    private loadUserFromLocalStorage(userDetail, resolve) {

        if (!this.auth.userDetail) {
            this.auth.userDetail = userDetail;
        }
        this.localStorage.get(userDetail.user._id + "-energyMonitors").subscribe((res: any) => {
            if (res) {
                this.user.energyMonitors = res.obj;
            }
        });
        this.localStorage.get(userDetail.user._id + "-batteryFeatureSets").subscribe((res: any) => {
            if (res) {
                this.user.batteryFeatureSets = res.obj;
            }
        });
        (this.localStorage.get(userDetail.user._id + '-lds-energy-featureSets') as any)
            .subscribe((res: any) => {
                if (!res || res.expires < Date.now()) {

                    this.initiateUser(userDetail, () => {
                        this.user.loading = false;
                        console.warn('[Energy] Saved data expired, loading fresh data from API');
                        resolve(true);
                    });
                } else {
                    this.initiateWs(userDetail);

                    const resValue = res.value || res;

                    this.energyDataService.featureSetsWithData = resValue.featureSetsWithData;
                    this.energyDataService.featureSetsWithoutData = resValue.featureSetsWithoutData;
                    this.user.allDevices = resValue.allDevices;
                    this.user.features = resValue.features;
                    this.user.motionFeatureSets = resValue.motionFeatureSets;
                    this.user.energyFeatureSets = resValue.energyFeatureSets;
                    this.user.rooms = resValue.rooms;
                    this.user.homes = resValue.homes;
                    this.user.deviceTypes = resValue.deviceTypes;
                    this.user.temperatureFeatureSets = resValue.temperatureFeatureSets;
                    this.user.switchFeatureSets = resValue.switchFeatureSets;
                    this.user._totalWeeklyUsage = resValue._totalWeeklyUsage;
                    this.user._totalMonthlyUsage = resValue._totalMonthlyUsage;

                    this.wsChatService.checkAuth(() => {
                        let wsPacket = this.wsPacketService.getPacket('user', 'read', {});
                        this.wsChatService.sendMsg({
                            data: wsPacket, next: (userSettings: WebSocketPacket) => {
                                this.user.settings = userSettings;
                            }
                        });

                        for (let i in this.user.allDevices) {
                            let device = this.user.allDevices[i];
                            if (device.productCode === 'VTRIGGER') {
                                this.user.addVirtualDevice(device);
                            }
                        }

                        this.user.loading = false;
                        resolve(true);
                    });

                }
            });
    }

    private readRootGroups(res, cb) {
        let payloadBuffer = [];
        for (let i = 0; i < res.items[0].payload.groupIds.length; i++) {
            let currentRootGroupId = res.items[0].payload.groupIds[i];
            payloadBuffer.push(this.wsPacketService.getPacket('group', 'read', {
                groupId: currentRootGroupId,
                features: true,
                subgroups: true,
                devices: true
            }));
        }
        this.wsChatService.sendMsgAsyc({
            data: payloadBuffer
            , next: (response) => {
                for (let i = 0; i < response.length; i++) {
                    let payload: any;
                    payload = response[i].items[0].payload;
                    this.user.addFeatures(payload.features);
                    this.user.addDevices(payload.devices);
                    for (const j in payload.devices) {
                        if (payload.devices.hasOwnProperty(j)) {
                            payload.devices[j].icon = this.lwProductService.findIcon(payload.devices[j].productCode);
                        }
                    }
                }
                this.groupHierarchy(response, cb);
            }
        });
    }

    private groupHierarchy(rootGroups, cb) {
        let promises = [];
        for (let index = 0; index < rootGroups.length; index++) {
            const payload = rootGroups[index].items[0].payload;
            const packet = this.wsPacketService.getPacket('group', 'hierarchy', { groupId: payload.groupId });
            promises.push(this.wsChatService._sendMsg(packet));
        }

        Promise.all(promises)
            .then((values) => {
                for (let index = 0; index < values.length; index++) {
                    const res = values[index];
                    let payload = res.items[0].payload;

                    for (const key in payload.featureSet) {
                        if (payload.featureSet.hasOwnProperty(key)) {
                            this.findEnergyFeature(payload.featureSet[key]);
                        }
                    }
                    this.user.addRooms(payload.room);

                    if (payload.home && payload.home[0]) {
                        let linkDeviceId = ''
                        const linkGroup = payload.link[0]

                        if (linkGroup && linkGroup.featureSets && linkGroup.featureSets.length) {
                            const found = payload.featureSet.find(fs => fs.groupId == linkGroup.featureSets[0])
                            if (found) {
                                linkDeviceId = found.deviceId
                            }
                        }

                        this.user.addHome(payload.root[0], linkDeviceId)
                    }
                }

                cb(true);
                
                const energySummaryFeatureSets = this.user.energySummaryFeatureSetIds.map(featureSetId => {
                    return this.user.energyFeatureSets[featureSetId]
                })
                
                this.analyticsService.loadEnergySummaryData(energySummaryFeatureSets)
            });
    }

    private findEnergyFeature(featureSet) {
        for (let i = 0; i < featureSet.features.length; i++) {
            let currentFeature = this.user.features[featureSet.features[i]];
            if (!currentFeature || !currentFeature.attributes) {
                continue;
            }
            if (currentFeature.attributes.type === "batteryLevel") {
                this.user.addBatteryFeatureSet({
                    id: currentFeature.attributes.featureId,
                    featureId: currentFeature.featureId,
                    deviceId: currentFeature.deviceId,
                    icon: this.demo.isDemo() ? featureSet.icon : this.user.devices[currentFeature.deviceId].icon,
                    productCode: this.demo.isDemo() ? featureSet.productCode : this.user.devices[currentFeature.deviceId].productCode,
                    groupId: featureSet.groupId,
                    name: featureSet.name,
                    linkId: currentFeature.featureId.split('-')[2].substring(0, 10),
                    selected: true,
                    features: featureSet.features,
                    batteryFeature: currentFeature.featureId
                });
            }
            if (this.user.devices[currentFeature.deviceId].productCode === "L70" || this.user.devices[currentFeature.deviceId].productCode === "L70MK2") {
                this.user.addMotionFeatureSet(featureSet.groupId, {
                    id: currentFeature.attributes.featureId,
                    featureId: currentFeature.featureId,
                    deviceId: currentFeature.deviceId,
                    icon: this.demo.isDemo() ? featureSet.icon : this.user.devices[currentFeature.deviceId].icon,
                    productCode: this.demo.isDemo() ? featureSet.productCode : this.user.devices[currentFeature.deviceId].productCode,
                    groupId: featureSet.groupId,
                    name: featureSet.name,
                    linkId: currentFeature.featureId.split('-')[2].substring(0, 10),
                    selected: true,
                    features: featureSet.features,
                    type: 'motion'
                });
                return;
            }

            if (currentFeature.attributes.type === 'energy' && this.user.devices[currentFeature.deviceId]) {
                const energyFeatureSet = {
                    id: currentFeature.attributes.featureId,
                    featureId: currentFeature.featureId,
                    deviceId: currentFeature.deviceId,
                    icon: this.demo.isDemo() ? featureSet.icon : this.user.devices[currentFeature.deviceId].icon,
                    productCode: featureSet.productCode,
                    virtualProductCode: featureSet.virtualProductCode || null,
                    groupId: featureSet.groupId,
                    name: featureSet.name,
                    linkId: currentFeature.featureId.split('-')[2].substring(0, 10),
                    selected: true,
                    features: featureSet.features,
                    type: 'energy'
                }
                this.user.addEnergyFeatureSet(featureSet.groupId, energyFeatureSet)

                if (this.user.isEnergyMonitor(energyFeatureSet)) {
                    console.log("handling isEnergyMonitor - ", featureSet.productCode, featureSet.virtualProductCode);
                    this.handleEnergyMonitor(energyFeatureSet);
                    return;
                }
            } 
            
            else if (currentFeature.attributes.type === 'temperature') {
                this.user.addTemperatureFeatureSet(featureSet.groupId, {
                    id: currentFeature.attributes.featureId,
                    deviceId: currentFeature.deviceId,
                    icon: this.demo.isDemo() ? featureSet.icon : this.user.devices[currentFeature.deviceId].icon,
                    productCode: this.demo.isDemo() ? featureSet.productCode : this.user.devices[currentFeature.deviceId].productCode,
                    virtualProductCode: this.user.devices[currentFeature.deviceId].virtualProductCode || null,
                    name: featureSet.name,
                    groupId: featureSet.groupId,
                    featureId: currentFeature.featureId,
                    linkId: currentFeature.featureId.split('-')[2].substring(0, 10),
                    selected: true,
                    features: featureSet.features,
                    created: new Date(currentFeature.createdDate).getTime(),
                    type: 'temperature'
                });
                this.user._temperatureFeatureSetsCount += 1;
            } 
            else if (currentFeature.attributes.type === 'switch' ||
                currentFeature.attributes.type === 'windowPosition' ||
                currentFeature.attributes.type === 'movement') {
                this.user.addSwitchFeatureSet(featureSet.groupId, {
                    id: currentFeature.attributes.featureId,
                    deviceId: currentFeature.deviceId,
                    icon: this.demo.isDemo() ? featureSet.icon : this.user.devices[currentFeature.deviceId].icon,
                    productCode: this.demo.isDemo() ? featureSet.productCode : this.user.devices[currentFeature.deviceId].productCode,
                    virtualProductCode: this.user.devices[currentFeature.deviceId].virtualProductCode || null,
                    name: featureSet.name,
                    groupId: featureSet.groupId,
                    featureId: currentFeature.featureId,
                    linkId: currentFeature.featureId.split('-')[2].substring(0, 10),
                    selected: true,
                    features: featureSet.features,
                    created: new Date(currentFeature.createdDate).getTime(),
                    type: 'switch'
                });
                this.user._switchFeatureCount += 1;
            }
        }
    }

    private handleEnergyMonitor(featureSet) {
        this.user.addEnergyMonitor(featureSet);
    }


}
